import { NgTemplateOutlet } from '@angular/common';
import { AfterViewChecked, Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild, computed } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  QaiCarouselComponent,
  QaiCarouselItemDirective,
  QaiButtonComponent,
  QaiLoadingComponent,
} from '@qai/ui';
import { PathwayCallHeaderComponent } from '../../call-pathway/call-header/call-header';
import { DeviceSettingsContentComponent } from '../../device-settings/device-settings-content/device-settings-content.component';
import { DeviceSettingsService } from '../../library/device-settings.service';
import { PathwaysService } from 'src/app/services/learner/pathways.service';
import { PathwayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { AssignmentService } from 'src/app/services/learner/assignment.service';
import { ICallDetailModal } from 'src/app/interfaces/icall-detail-modal';
import { parseTabText } from 'src/app/utils/parse-tab-text';
import { ControlService } from '../../library/control.service';

@Component({
  selector: 'app-assessment-pre-call',
  standalone: true,
  host: { class: 'theme-dark' },
  imports: [
    NgTemplateOutlet,
    QaiCarouselComponent,
    QaiCarouselItemDirective,
    QaiButtonComponent,
    QaiLoadingComponent,
    PathwayCallHeaderComponent,
    DeviceSettingsContentComponent,
  ],
  templateUrl: './assessment-pre-call.component.html',
  styleUrls: ['./assessment-pre-call.component.scss'],
})
export class AssessmentPreCallComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild(QaiCarouselComponent) private carousel?: QaiCarouselComponent;
  @ViewChild(DeviceSettingsContentComponent, { read: ElementRef }) private deviceSettingsEl?: ElementRef<HTMLElement>;

  private heightObserver?: ResizeObserver;
  private heightObserverReady = false;
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly pathwaysService = inject(PathwaysService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpointSub: Subscription;
  readonly deviceSettingsService = inject(DeviceSettingsService);

  private readonly assignmentService = inject(AssignmentService);
  private readonly controlService = inject(ControlService);

  readonly isMobile = signal(false);
  readonly isDiagnosticRunning = signal(false);
  readonly isEnrollmentLoading = signal(false);
  readonly callDetails = signal<ICallDetailModal | null>(null);
  readonly isCallDetailsLoading = signal(true);

  readonly isBeginDisabled = computed(
    () => this.isEnrollmentLoading() || this.isCallDetailsLoading() || this.isDiagnosticRunning()
  );

  readonly parsedTabs = computed(() =>
    [...(this.callDetails()?.callDetails ?? [])]
      .filter((tab) => tab.tabTitle?.trim() && tab.tabText?.trim())
      .sort((a, b) => a.tabSequence - b.tabSequence)
      .map((tab) => ({ ...tab, blocks: parseTabText(tab.tabText) }))
  );

  private moduleAssignmentUuid: string | null = null;
  private practiceType = 'PATHWAY';
  private enrollmentUuid: string | null = null;
  private enrollmentStepUuid: string | null = null;

  constructor() {
    const bp = inject(BreakpointObserver);
    this.breakpointSub = bp.observe('(max-width: 768px)').subscribe((state) => {
      this.isMobile.set(state.matches);
    });
  }

  ngOnInit(): void {
    this.controlService.isRealTimeCall = true;
    this.deviceSettingsService.startDeviceCheck();

    const params = this.route.snapshot.queryParams;
    this.moduleAssignmentUuid = params['moduleAssignmentUuid'] ?? null;
    this.practiceType = params['practiceType'] ?? 'PATHWAY';
    this.enrollmentUuid = params['enrollmentUuid'] ?? null;
    this.enrollmentStepUuid = params['enrollmentStepUuid'] ?? null;

    if (this.enrollmentUuid && this.enrollmentStepUuid) {
      this.fetchCallDetails();
      return;
    }

    if (!this.moduleAssignmentUuid) return;

    this.isEnrollmentLoading.set(true);

    const call$ = this.enrollmentUuid
      ? this.pathwaysService.getorCreatePathwayEnrollment(this.moduleAssignmentUuid)
      : this.pathwaysService.createEnrollment(this.moduleAssignmentUuid);

    call$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (enrollment) => {
        this.enrollmentUuid = enrollment.enrollmentUuid;
        const allSteps = enrollment.focusAreaGroups.flatMap(g => g.steps);
        const assessmentStep = allSteps.find(s => s.type === PathwayStepType.ASSESSMENT);
        this.enrollmentStepUuid =
          assessmentStep?.enrollmentStepUuid ?? enrollment.activeEnrollmentStepUuid;
        this.isEnrollmentLoading.set(false);
        this.fetchCallDetails();
      },
      error: () => {
        this.isEnrollmentLoading.set(false);
      },
    });
  }

  private fetchCallDetails(): void {
    if (!this.moduleAssignmentUuid || !this.enrollmentStepUuid) {
      this.isCallDetailsLoading.set(false);
      return;
    }

    this.isCallDetailsLoading.set(true);

    this.assignmentService
      .getCallDetailsByEnrollmentStepUuid(this.enrollmentStepUuid, this.moduleAssignmentUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (details: ICallDetailModal) => {
          this.callDetails.set(details);
          this.isCallDetailsLoading.set(false);
        },
        error: () => {
          this.isCallDetailsLoading.set(false);
        },
      });
    }
    
  ngAfterViewChecked(): void {
    if (!this.heightObserverReady && this.carousel && this.deviceSettingsEl?.nativeElement) {
      this.heightObserverReady = true;
      requestAnimationFrame(() => this.carousel?.recalculate());
      this.watchDeviceSettingsHeight();
    }
  }

  ngOnDestroy(): void {
    this.breakpointSub.unsubscribe();
    this.heightObserver?.disconnect();
    this.controlService.isRealTimeCall = false;
  }

  private watchDeviceSettingsHeight(): void {
    if (!this.deviceSettingsEl?.nativeElement || !this.carousel) return;
    this.heightObserver = new ResizeObserver(() => this.carousel?.recalculate());
    this.heightObserver.observe(this.deviceSettingsEl.nativeElement);
  }

  handleClose(): void {
    this.router.navigate(['/assignments']);
  }

  handleBeginSession(): void {
    this.deviceSettingsService.stopDeviceSettings(true);

    if (!this.enrollmentUuid || !this.enrollmentStepUuid || !this.moduleAssignmentUuid) return;

    this.router.navigate(['/assessment-call'], {
      queryParams: {
        enrollmentUuid: this.enrollmentUuid,
        moduleAssignmentUuid: this.moduleAssignmentUuid,
        enrollmentStepUuid: this.enrollmentStepUuid,
        practiceType: this.practiceType,
      },
    });
  }
}
